const OBJECT_KEY_SEPARATOR = ".";

/**
 * Формирует из строки вида "key1.key2.key3..." объект =>
 * {
 *     key1: {
 *         key2: {
 *             key3: {
 *                 ...
 *             }
 *         }
 *     }
 * }
 * @param str - строка с ключами, разделённые точкой (OBJECT_KEY_SEPARATOR)
 * @param value - значение, которое следует установить конечному ключу. По-умолчанию, undefined
 */
export const getObjectFromString = <T>(str: string, value?: T) => {
    const arr = str.split(OBJECT_KEY_SEPARATOR);

    const fillObject = (
        obj: Record<string, any>,
        index = 0,
    ): Record<string, any> | null => {
        if (index > arr.length - 1) {
            return null;
        }
        obj[arr[index]] = index === arr.length - 1 ? value : {};
        return fillObject(obj[arr[index]], index + 1);
    };

    const obj = {};
    fillObject(obj);
    return obj;
};

export const isObject = <T>(item: T) => {
    return item && typeof item === "object" && !Array.isArray(item);
};

/**
 * Сливает target и source в один объект. Работает как Object.assign, но с глубоким копированием полей:
 * const obj1 = { a: { key1: 1 } };
 * const obj2 = { a: { key2: 2 } };
 * =>
 * {
 *     a: {
 *         key1: 1,
 *         key2: 2,
 *     }
 * }
 * @param target
 * @param source
 */
export const objectMergeDeep = <
    T1 extends Record<string, any>,
    T2 extends Record<string, any>,
>(
    target: T1,
    source: T2,
) => {
    const result = Object.assign({}, target) as Record<string, any>;

    Object.keys(source).forEach((_key) => {
        const key = _key as string;
        const currentValue = source[key];
        if (isObject(currentValue)) {
            if (!(key in target)) {
                Object.assign(result, {
                    [key]: currentValue,
                });
            } else {
                result[key] = objectMergeDeep(target[key], currentValue);
            }
        } else {
            Object.assign(result, {
                [key]: currentValue,
            });
        }
    });

    return result;
};

/**
 * @description
 * Генерирует массив, состоящий из чисел, каждое из которых соответствует своему индексу в массиве
 * @param length - размер массива
 */
export const generateFilledByIndexArray = (length: number): number[] =>
    Array.from({ length }).map((_, idx) => idx);
